/**
 * Calculadora de frete na página de produto (Made In God).
 *
 * A Shopify não expõe cotação de frete por produto isolado: o endpoint
 * /cart/shipping_rates.json cota sempre o carrinho da sessão. Para cotar apenas
 * este produto, o fluxo é:
 *
 *   1. snapshot do carrinho  (GET  /cart.js)
 *   2. adiciona o produto    (POST /cart/add.js)
 *   3. cota o frete          (GET  /cart/shipping_rates.json)
 *   4. restaura a quantidade original da linha (POST /cart/change.js)
 *
 * O passo 4 roda em finally e também em pagehide (via sendBeacon), para o caso
 * de a pessoa sair da página no meio da cotação. Nenhum evento on:cart:change é
 * disparado, então o contador do header não pisca.
 */
if (!customElements.get('product-shipping-calculator')) {
  class ProductShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.zipInput = this.querySelector('.js-psc-zip');
      this.button = this.querySelector('.js-psc-submit');
      this.ratesEl = this.querySelector('.js-psc-rates');
      this.errorEl = this.querySelector('.js-psc-error');

      this.variantId = this.dataset.variantId || null;
      this.pendingRestore = null;
      this.config = ProductShippingCalculator.readConfig(this);

      this.button.addEventListener('click', this.handleSubmit.bind(this));
      this.zipInput.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') {
          evt.preventDefault();
          this.handleSubmit(evt);
        }
      });
      this.zipInput.addEventListener('input', this.maskZip.bind(this));

      document.addEventListener('on:variant:change', (evt) => {
        const variant = evt.detail && evt.detail.variant;
        this.variantId = variant ? String(variant.id) : null;
        this.reset();
      });

      window.addEventListener('pagehide', this.restoreOnUnload.bind(this));
    }

    get cartRoute() {
      return (window.theme && theme.routes && theme.routes.cart) || '/cart';
    }

    /**
     * Le o JSON de configuracao embutido no elemento.
     * @param {HTMLElement} root - O proprio custom element.
     * @returns {object} Config com moneyFormat e strings.
     */
    static readConfig(root) {
      const el = root.querySelector('.js-psc-config');
      if (!el) return { moneyFormat: null, strings: {} };
      try {
        const parsed = JSON.parse(el.textContent);
        return { moneyFormat: parsed.moneyFormat || null, strings: parsed.strings || {} };
      } catch (error) {
        console.warn('Config da calculadora de frete invalida.', error); // eslint-disable-line
        return { moneyFormat: null, strings: {} };
      }
    }

    t(key, fallback) {
      return (this.config && this.config.strings && this.config.strings[key]) || fallback;
    }

    /** Aplica a máscara 00000-000 enquanto a pessoa digita. */
    maskZip() {
      const digits = this.zipInput.value.replace(/\D/g, '').slice(0, 8);
      this.zipInput.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }

    reset() {
      this.ratesEl.hidden = true;
      this.errorEl.hidden = true;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.ratesEl.hidden = true;
    }

    async handleSubmit(evt) {
      if (evt) evt.preventDefault();
      this.reset();

      const zip = this.zipInput.value.replace(/\D/g, '');
      if (zip.length !== 8 || !this.stateFromZip(zip)) {
        this.showError(this.t('invalidZip', 'CEP inválido.'));
        return;
      }

      if (!this.variantId) {
        this.showError(this.t('unavailable', 'Produto indisponível.'));
        return;
      }

      this.button.disabled = true;
      this.button.classList.add('is-loading');

      try {
        const rates = await this.fetchRates(zip);
        this.showRates(rates, zip);
      } catch (error) {
        this.showError(error.message || this.t('error', 'Não foi possível calcular o frete.'));
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }

    /**
     * Cota o frete para este produto sem deixar rastro no carrinho.
     * @param {string} zip - CEP apenas com dígitos.
     * @returns {Promise<Array>} Lista de tarifas.
     */
    async fetchRates(zip) {
      const quantity = this.getQuantity();

      // 1. snapshot: quantidades por linha antes de mexer no carrinho
      const before = await this.getCart();
      const quantityByKey = {};
      before.items.forEach((item) => {
        quantityByKey[item.key] = item.quantity;
      });

      // 2. adiciona o produto
      const addResponse = await fetch(`${this.cartRoute}/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.variantId), quantity }] })
      });
      const added = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(added.description || added.message || this.t('error', ''));
      }

      const line = added.items && added.items[0];
      const key = line && line.key;
      const restoreTo = key && Object.prototype.hasOwnProperty.call(quantityByKey, key) ? quantityByKey[key] : 0;

      // Guarda o alvo de restauração para o caso de a página fechar no meio.
      if (key) this.pendingRestore = { id: key, quantity: restoreTo };

      try {
        // 3. cota o frete
        // A Shopify recusa a cotação de endereço brasileiro sem estado (422,
        // "Selecione um estado"). A UF sai da faixa do CEP, sem perguntar ao cliente.
        const params = new URLSearchParams();
        params.set('shipping_address[zip]', zip);
        params.set('shipping_address[country]', 'Brazil');
        params.set('shipping_address[province]', this.stateFromZip(zip));

        const response = await fetch(`${this.cartRoute}/shipping_rates.json?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        });
        const data = await response.json();

        if (!response.ok) {
          const firstKey = Object.keys(data)[0];
          throw new Error(firstKey ? [].concat(data[firstKey]).join(' ') : this.t('error', ''));
        }

        return data.shipping_rates || [];
      } finally {
        // 4. restaura o carrinho
        if (key) {
          await this.restoreLine(key, restoreTo);
          this.pendingRestore = null;
        }
      }
    }

    /**
     * Sigla do estado a partir do CEP, pelas faixas dos Correios.
     * @param {string} zip - CEP apenas com dígitos.
     * @returns {string|null} Sigla da UF, ou null se o CEP não pertence a nenhuma faixa.
     */
    stateFromZip(zip) {
      const n = parseInt(zip.slice(0, 5), 10);
      const ranges = [
        [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
        [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
        [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
        [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
        [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
        [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
        [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
        [88000, 89999, 'SC'], [90000, 99999, 'RS']
      ];
      const hit = ranges.find(([min, max]) => n >= min && n <= max);
      return hit ? hit[2] : null;
    }

    async getCart() {
      const response = await fetch(`${this.cartRoute}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(this.t('error', ''));
      return response.json();
    }

    async restoreLine(key, quantity) {
      try {
        await fetch(`${this.cartRoute}/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
      } catch (error) {
        console.warn('Falha ao restaurar o carrinho apos a cotacao de frete.', error); // eslint-disable-line
      }
    }

    /** Último recurso: restaura a linha mesmo se a pessoa fechar a página. */
    restoreOnUnload() {
      if (!this.pendingRestore || !navigator.sendBeacon) return;
      const payload = new Blob([JSON.stringify(this.pendingRestore)], { type: 'application/json' });
      navigator.sendBeacon(`${this.cartRoute}/change.js`, payload);
      this.pendingRestore = null;
    }

    getQuantity() {
      const input = document.querySelector('.js-product-form input[name="quantity"]');
      const value = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      const format = (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) || '{{amount}}';
      return format.replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2));
    }

    /**
     * @param {Array} rates - Tarifas devolvidas pela Shopify.
     * @param {string} zip - CEP consultado.
     */
    showRates(rates, zip) {
      const headingEl = this.ratesEl.querySelector('.js-psc-rates-heading');
      const textEl = this.ratesEl.querySelector('.js-psc-rates-text');

      if (!rates.length) {
        headingEl.hidden = true;
        textEl.textContent = this.t('noRates', 'Nao encontramos frete para este CEP.');
        this.ratesEl.hidden = false;
        return;
      }

      const formattedZip = `${zip.slice(0, 5)}-${zip.slice(5)}`;
      headingEl.hidden = false;
      headingEl.textContent = this.t('results', 'Opcoes de entrega:').replace('__ZIP__', formattedZip);

      const items = rates.map((rate) => {
        const price = this.formatMoney(Math.round(parseFloat(rate.price) * 100));
        const days = rate.delivery_days && rate.delivery_days.length
          ? ` &mdash; ${this.deliveryText(rate.delivery_days)}`
          : '';
        return `<li>${ProductShippingCalculator.escapeHtml(rate.presentment_name || rate.name)}: ${price}${days}</li>`;
      });

      textEl.innerHTML = `<ul class="styled-list">${items.join('')}</ul>`;
      this.ratesEl.hidden = false;
    }

    deliveryText(deliveryDays) {
      const max = Math.max.apply(null, deliveryDays);
      const template = max === 1 ? this.t('dayOne', '__COUNT__ dia util') : this.t('dayOther', '__COUNT__ dias uteis');
      return template.replace('__COUNT__', max);
    }

    static escapeHtml(value) {
      const el = document.createElement('span');
      el.textContent = value == null ? '' : String(value);
      return el.innerHTML;
    }
  }

  customElements.define('product-shipping-calculator', ProductShippingCalculator);
}
